import { writable } from 'svelte/store'

type Media<Query extends Record<string, string> = Record<string, string>> = {
	[K in keyof Query]?: boolean | string
}

// No deprecated listeners for Safari
interface MediaQueryList extends EventTarget {
	readonly matches: boolean
	readonly media: string
	onchange: ((this: MediaQueryList, ev: MediaQueryListEvent) => any) | null
	addListener(callback: ((this: MediaQueryList, ev: MediaQueryListEvent) => any) | null): void
	removeListener(callback: ((this: MediaQueryList, ev: MediaQueryListEvent) => any) | null): void
	addEventListener<K extends keyof MediaQueryListEventMap>(
		type: K,
		listener: (this: MediaQueryList, ev: MediaQueryListEventMap[K]) => any,
		options?: boolean | AddEventListenerOptions,
	): void
	addEventListener(
		type: string,
		listener: EventListenerOrEventListenerObject,
		options?: boolean | AddEventListenerOptions,
	): void
	removeEventListener<K extends keyof MediaQueryListEventMap>(
		type: K,
		listener: (this: MediaQueryList, ev: MediaQueryListEventMap[K]) => any,
		options?: boolean | EventListenerOptions,
	): void
	removeEventListener(
		type: string,
		listener: EventListenerOrEventListenerObject,
		options?: boolean | EventListenerOptions,
	): void
}

type MediaQueryLists = Record<string, MediaQueryList>

function calculateMedia(mql: MediaQueryLists) {
	const media: Media = {}
	for (const name in mql) {
		media[name] = mql[name].matches
	}
	return media
}

function getMediaQueries<Query extends Record<string, string>>(mediaQueries: Query) {
	return writable<Media<Query>>({}, (set) => {
		if (typeof window === 'undefined') return

		const mql: MediaQueryLists = {}
		function updateMedia() {
			return set(calculateMedia(mql))
		}

		for (const key in mediaQueries) {
			mql[key] = window.matchMedia(mediaQueries[key])
			mql[key].addListener(updateMedia) // deprecated addListener for Safari
		}

		updateMedia()

		return () => {
			for (const key in mql) {
				mql[key].removeListener(updateMedia)
			}
		}
	})
}

const mediaQueries = {
	// same as Tailwind breakpoints
	maxMd: '(max-width: 767px)',
	maxLg: '(max-width: 1023px)',
	md: '(min-width: 768px)',
	lg: '(min-width: 1024px)',
	xl: '(min-width: 1280px)',
	dark: '(prefers-color-scheme: dark)',
}

export const media = getMediaQueries(mediaQueries)
